<template>
  <component
    :is="isMobile ? VBottomSheet : SideMenu"
    :value="value"
    v-bind="isMobile ? {
      fullscreen: true,
    } : {
      useAlternateContentTemplate: true,
    }"
    v-on="isMobile ? {} : {
      'side-menu:click-close': () => emit('input', false),
    }"
    @input="emit('input', $event)"
  >
    <div class="d-flex flex-column bg-gray-0 h-100">
      <div>
        <div class="px-2 py-4">
          <v-icon @click="emit('input', false)">
            ui-icon-close-not-a-circle
          </v-icon>
        </div>
        <v-text-field
          :value="searchText"
          background-color="bg-gray-40"
          color="text-info"
          clear-icon="ui-icon-close-not-a-circle"
          prepend-inner-icon="ui-icon-search"
          :label="t('Специальность или услуга', options)"
          class="px-2 mb-2"
          clearable
          hide-details
          solo
          flat
          @input="debounceUpdateSearchText"
        />
      </div>
      <v-divider />
      <div class="flex-grow-1 overflow-y-auto">
        <template v-if="workplacesList.length || servicesList.length">
          <ContentList
            :title="t('Приём', options)"
            :lpu-id="lpuId"
            :class="{ 'mb-4': servicesList.length }"
            :list="workplacesList"
            :is-lpu-appointment-type-chosen="isLpuAppointmentTypeChosen"
            @click-item="handleClickItem"
          />
          <ContentList
            v-if="filterType === 'clinic'"
            title="Услуги"
            :lpu-id="lpuId"
            :list="servicesList"
            is-lpu-appointment-type-chosen
            @click-item="handleClickItem"
          />
        </template>
        <div
          v-else
          class="text-center py-16 px-2 my-6"
        >
          <img
            src="/static/_v1/pd/pictures/ui-kit/search.svg"
            :alt="t('Поиск', options)"
            width="150"
            height="150"
            class="mb-2"
          >
          <div class="text-body-1 text--text">
            {{ t('Ничего не найдено', options) }}
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import I18NextVue from 'i18next'
import { debounce, getFormattedPrice } from 'www/utils'
import { getWorkplaceNonNullPrices, getSpecialityAgesText } from 'www/vue_components/common/DoctorPageLpuList/components/DoctorAppointmentCard/functions'
import ContentList from 'www/vue_components/common/DoctorPageLpuList/components/DoctorAppointmentCard/components/WorkplaceOrServiceDialog/components/ContentList.vue'
import { VBottomSheet } from 'vuetify/lib'
import SideMenu from 'www/vue_components/desktop/core/SideMenu.vue'
import { useDoctorPageLpuListStore } from 'www/vue_components/common/DoctorPageLpuList/stores'
import { useDoctorAppointmentCardStore } from 'www/vue_components/common/DoctorPageLpuList/components/DoctorAppointmentCard/stores'
import type { SelectWorkplaceOrServiceItem } from 'www/vue_components/common/DoctorPageLpuList/types'
import { getPriceList } from 'www/vue_components/common/DoctorPageLpuList/functions'

const isMobile = window.MOBILE_VERSION

const props = defineProps<{
  value: boolean
  lpuId: number
}>()

const { t, language } = I18NextVue
const options = { ns: language }

const emit = defineEmits(['input', 'item-selected'])

const searchText = ref('')

const doctorPageLpuListStore = useDoctorPageLpuListStore()
const { filterType } = storeToRefs(doctorPageLpuListStore)

const doctorAppointmentCardStore = useDoctorAppointmentCardStore(props.lpuId)()
const { workplacesWithSelectedFilterType, services } = storeToRefs(doctorAppointmentCardStore)

const isLpuAppointmentTypeChosen = computed(() => filterType.value === 'clinic')

function checkIncludeSearchText(text: string) {
  return text.toLowerCase().includes(searchText.value.toLowerCase())
}

const workplacesFiltered = computed(() => {
  if (!searchText.value) {
    return workplacesWithSelectedFilterType.value
  }

  return workplacesWithSelectedFilterType.value.filter(
    ({ spec }) => checkIncludeSearchText(spec.name),
  )
})

function updateSearchText(text: string) {
  searchText.value = text || ''
}

const servicesFiltered = computed(() => {
  if (!searchText.value) {
    return services.value
  }

  return services.value.filter(({ name }) => checkIncludeSearchText(name))
})

const workplacesList = computed<SelectWorkplaceOrServiceItem[]>(() => workplacesFiltered.value.map(workplace => {
  const notNullPrices = getWorkplaceNonNullPrices(getPriceList(workplace))

  return {
    workplace,
    price: getFormattedPrice(Math.min(...notNullPrices)),
    agesText: getSpecialityAgesText({
      min: workplace.age_min,
      max: workplace.age_max,
    }),
    isWorkplace: true,
  }
}))

const servicesList = computed<SelectWorkplaceOrServiceItem[]>(() => servicesFiltered.value.map(service => ({
  service,
  price: service.price ? getFormattedPrice(service.price) : null,
  agesText: getSpecialityAgesText({
    min: service.age_min,
    max: service.age_max,
  }),
  isWorkplace: false,
})))

const debounceUpdateSearchText = debounce(updateSearchText, 200)

function handleClickItem(item: SelectWorkplaceOrServiceItem) {
  if (!props.value) {
    return
  }

  emit('item-selected', item)
  emit('input', false)
}

watch(() => props.value, newValue => {
  if (!newValue) {
    searchText.value = ''
  }
})
</script>

<style scoped lang="scss">
// Потому что есть глобальные стили с селектором `#app input`, которые ломают отображение
:deep(input) {
  padding: 8px 0 !important;
}
</style>
